import React, { useState, useEffect } from 'react';
import {
  Target, Sparkles, Layers, CalendarDays, BrainCircuit, Trophy, Medal, Flame,
  Bell, Clock, ShieldCheck, Award, Plus, X, ImagePlus, Save, UserCheck, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';
import { PageShell } from '../components/PageShell';
import { SectionCard, Button, Badge } from '../components/primitives';
import { Toggle } from '../components/Toggle';
import { Input, Textarea } from '../components/forms';
import { useDashTheme } from '../hooks/useDashTheme';

// ── Small local pieces (presentation only — all state lives in GoalsSettingsContext) ──

// One settings row: title + description on the left, control on the right.
function Row({ title, desc, children, last }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: t.s[4],
      padding: `${t.s[3]}px 0`, borderBottom: last ? 'none' : `1px solid ${t.c.border}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text }}>{title}</div>
        {desc && <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, lineHeight: 1.45 }}>{desc}</div>}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: t.s[2] }}>{children}</div>
    </div>
  );
}

// Compact labelled number input with an optional unit suffix.
function NumField({ label, value, onChange, onBlur, min, max, suffix, placeholder, width = '100%' }) {
  const t = useDashTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width }}>
      {label && <span style={{ fontSize: t.font.size.sm, fontWeight: 500, color: t.c.textSecondary }}>{label}</span>}
      <div style={{ position: 'relative' }}>
        <Input
          type="number"
          value={value ?? ''}
          placeholder={placeholder}
          min={min}
          max={max}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === '' ? null : Number(raw));
          }}
          onBlur={onBlur}
          style={{ paddingRight: suffix ? 44 : 12, fontVariantNumeric: 'tabular-nums' }}
        />
        {suffix && (
          <span style={{
            position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)',
            fontSize: t.font.size.xs, color: t.c.textTertiary, pointerEvents: 'none',
          }}>{suffix}</span>
        )}
      </div>
    </div>
  );
}

// Icon tile with upload / remove for badges & streak.
function IconTile({ url, alt, onUpload, onRemove, size = 32 }) {
  const t = useDashTheme();
  return url ? (
    <span style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'inline-flex' }}>
      <img src={url} alt={alt} style={{ width: size, height: size, borderRadius: t.r.sm, objectFit: 'cover' }} />
      <button
        onClick={onRemove}
        title="Remove icon"
        style={{
          position: 'absolute', top: -5, right: -5, width: 15, height: 15, borderRadius: 99,
          border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
        }}
      >
        <X size={9} strokeWidth={3} />
      </button>
    </span>
  ) : (
    <label
      title="Upload icon"
      style={{
        width: size, height: size, borderRadius: t.r.sm, flexShrink: 0, cursor: 'pointer',
        border: `1px dashed ${t.c.borderStrong}`, background: t.c.surfaceMuted, color: t.c.textTertiary,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <ImagePlus size={size * 0.45} />
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
          if (file.size > 5 * 1024 * 1024) { toast.error('File size must be less than 5MB'); return; }
          try { await onUpload(file); } catch (err) { console.error('Icon upload failed:', err); }
        }}
      />
    </label>
  );
}

// Cycle-window card: start day + duration for one phase of the month.
function WindowCard({ icon, accent, title, desc, value, onChange, startDefault, durationDefault, maxDuration = 31, hint, clearable }) {
  const t = useDashTheme();
  const pair = t.accentPairs[accent];
  const win = value || {};
  const set = (patch) => onChange({ ...(value || { startDay: startDefault, duration: durationDefault }), ...patch });
  return (
    <div style={{ border: `1px solid ${t.c.border}`, borderRadius: t.r.lg, padding: t.s[4], background: t.c.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2], marginBottom: 4 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: t.r.sm, background: pair.bg, color: pair.fg, flexShrink: 0,
        }}>{icon}</span>
        <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.text }}>{title}</span>
      </div>
      <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, lineHeight: 1.45, marginBottom: t.s[3] }}>{desc}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: t.s[3] }}>
        <NumField
          label="Start day"
          value={win.startDay ?? null}
          placeholder={clearable ? '—' : undefined}
          min={1} max={31} suffix="day"
          onChange={(v) => set({ startDay: clearable ? (v || null) : v })}
          onBlur={() => { if (!clearable && !win.startDay) set({ startDay: startDefault }); }}
        />
        <NumField
          label="Duration"
          value={win.duration ?? null}
          placeholder={clearable ? '—' : undefined}
          min={1} max={maxDuration} suffix="days"
          onChange={(v) => set({ duration: clearable ? (v || null) : v })}
          onBlur={() => { if (!clearable && !win.duration) set({ duration: durationDefault }); }}
        />
      </div>
      {hint && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: t.s[2], lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

const GoalsSettingsV2 = () => {
  const t = useDashTheme();
  const {
    settings: goalsSettings, updateSettings, updateSoftSkill, addSoftSkill, removeSoftSkill,
    updateBadgeRange, updateScoreRange, saveSettings, uploadBadgeIcon, uploadStreakIcon,
    removeBadgeIcon, removeStreakIcon, hasUnsavedChanges, resetToSaved,
  } = useGoalsSettings();

  const [saving, setSaving] = useState(false);

  const mode = goalsSettings.goalsMode || 'classic';
  const isCycleMode = mode === 'classic' || mode === 'star';

  const modeOptions = [
    { id: 'classic', label: 'Classic cycle', desc: 'Submit → review → evaluate. Achieved % maps to a 1–10 score.' },
    { id: 'star', label: 'Star cycle', desc: 'Submit → review (manager sets points) → complete → verify. Score out of 5 ★.' },
    { id: 'score_only', label: 'Score only', desc: 'No windows or approvals. People add goals and record achievement any time.' },
    { id: 'checklist', label: 'Checklist', desc: 'No scoring at all. People add goals and simply check them off.' },
  ];

  // Warn on tab close / refresh with unsaved edits.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings(); // context handles success/error toasts + validation
    } catch (error) {
      console.error('Failed to save goal policy:', error);
    } finally {
      setSaving(false);
    }
  };

  const sectionGap = { display: 'flex', flexDirection: 'column', gap: t.s[5] };

  return (
    <PageShell
      eyebrow="Performance · Goals"
      title="Goals Settings"
      subtitle="How goals work for your whole organisation — mode, cycle rules, schedule and recognition."
      icon={<Target size={22} />}
      actions={(
        <>
          {hasUnsavedChanges && <Badge tone="warning" dot>Unsaved changes</Badge>}
          {hasUnsavedChanges && (
            <Button variant="ghost" onClick={resetToSaved} disabled={saving}>Discard</Button>
          )}
          <Button
            variant="primary"
            leftIcon={<Save size={14} />}
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      )}
    >
      <div style={{ maxWidth: 980, ...sectionGap }}>

        {/* ── Goals mode ── */}
        <SectionCard
          icon={<Sparkles size={14} />} accent="purple"
          title="Goals mode"
          subtitle="One choice that shapes the whole module — only the settings relevant to this mode are shown below."
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: t.s[3] }}>
            {modeOptions.map((opt) => {
              const active = mode === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => updateSettings({ goalsMode: opt.id })}
                  style={{
                    textAlign: 'left', padding: t.s[3], borderRadius: t.r.md, cursor: 'pointer',
                    background: active ? t.c.primarySoft : t.c.surface,
                    border: `1px solid ${active ? t.c.primary : t.c.border}`,
                    boxShadow: active ? `0 0 0 3px ${t.c.primaryRing}` : 'none',
                    transition: 'border-color 140ms, box-shadow 140ms, background 140ms',
                    fontFamily: t.font.family,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{
                      fontSize: t.font.size.base, fontWeight: t.font.weight.semibold,
                      color: active ? t.c.primary : t.c.text,
                    }}>{opt.label}</span>
                    {active && <Check size={14} style={{ color: t.c.primary, flexShrink: 0 }} />}
                  </span>
                  <span style={{ display: 'block', fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5 }}>
                    {opt.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </SectionCard>

        {/* ── Modules ── */}
        <SectionCard
          icon={<Layers size={14} />} accent="blue"
          title="Modules"
          subtitle="Which goal-related areas appear in the sidebar for your people."
        >
          <Row title="My Goals" desc="The goals module itself — turning this off hides Goals for everyone.">
            <Toggle on={goalsSettings.myGoalsEnabled} onClick={() => updateSettings({ myGoalsEnabled: !goalsSettings.myGoalsEnabled })} label="My Goals" />
          </Row>
          <Row title="My Tasks" desc="Task sync integrations alongside goals." last>
            <Toggle on={goalsSettings.myTasksEnabled} onClick={() => updateSettings({ myTasksEnabled: !goalsSettings.myTasksEnabled })} label="My Tasks" />
          </Row>
        </SectionCard>

        {/* ── Cycle rules ── */}
        <SectionCard
          icon={<Target size={14} />} accent="green"
          title="Goal rules"
          subtitle={mode === 'checklist'
            ? 'How many goals each person adds per month.'
            : 'How many goals each person adds per month, and how weightage is distributed.'}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: t.s[3] }}>
            <NumField
              label="Minimum goals" value={goalsSettings.minGoals} min={1} max={goalsSettings.maxGoals}
              onChange={(v) => updateSettings({ minGoals: v || 1 })}
            />
            <NumField
              label="Maximum goals" value={goalsSettings.maxGoals} min={goalsSettings.minGoals}
              onChange={(v) => updateSettings({ maxGoals: v || goalsSettings.minGoals })}
            />
            <NumField
              label="Critical goals required" value={goalsSettings.criticalRequired} min={0} max={goalsSettings.minGoals}
              onChange={(v) => updateSettings({ criticalRequired: v || 0 })}
            />
          </div>

          {mode !== 'checklist' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: t.s[3], marginTop: t.s[4], paddingTop: t.s[4], borderTop: `1px solid ${t.c.border}` }}>
                <NumField
                  label="Min weightage" value={goalsSettings.minWeightage} min={10} max={goalsSettings.maxWeightage ?? 100} suffix="%"
                  onChange={(v) => updateSettings({ minWeightage: v != null ? v : 10 })}
                  onBlur={() => {
                    const raw = goalsSettings.minWeightage;
                    if (raw == null || isNaN(raw)) return;
                    const maxW = goalsSettings.maxWeightage ?? 100;
                    const normalized = mode === 'star' ? Math.round(Number(raw)) : Math.round(Number(raw) / 10) * 10;
                    const clamped = Math.max(1, Math.min(maxW, normalized));
                    if (clamped !== raw) updateSettings({ minWeightage: clamped });
                  }}
                />
                <NumField
                  label="Max weightage" value={goalsSettings.maxWeightage} min={goalsSettings.minWeightage ?? 10} max={100} suffix="%"
                  onChange={(v) => updateSettings({ maxWeightage: v != null ? v : 100 })}
                  onBlur={() => {
                    const raw = goalsSettings.maxWeightage;
                    if (raw == null || isNaN(raw)) return;
                    const minW = goalsSettings.minWeightage ?? 10;
                    const normalized = mode === 'star' ? Math.round(Number(raw)) : Math.round(Number(raw) / 10) * 10;
                    const clamped = Math.max(minW, Math.min(100, normalized));
                    if (clamped !== raw) updateSettings({ maxWeightage: clamped });
                  }}
                />
              </div>
              <p style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, margin: `${t.s[3]}px 0 0`, lineHeight: 1.5 }}>
                {mode === 'star'
                  ? 'In star mode the manager assigns points during review — any whole number within range. Non-critical goals must total exactly 100 points.'
                  : 'Per-goal weightage must be a multiple of 10. The sum of non-critical goals must equal 100%.'}
                {' '}Critical goals count toward the maximum: with a max of {goalsSettings.maxGoals} and {goalsSettings.criticalRequired} critical required, totals must stay between {goalsSettings.minGoals} and {goalsSettings.maxGoals}.
              </p>
            </>
          )}
        </SectionCard>

        {/* ── Score mapping — modes that map achieved % to a 1–10 score ── */}
        {(mode === 'classic' || mode === 'score_only') && (
          <SectionCard
            icon={<Award size={14} />} accent="aqua"
            title="Score from achieved %"
            subtitle="During evaluation the entered achieved % auto-fills the 1–10 score from the range it falls in."
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: t.s[3] }}>
              {(goalsSettings.scoreRanges || []).map((range, idx) => (
                <div key={idx} style={{
                  border: `1px solid ${t.c.border}`, borderRadius: t.r.md, padding: t.s[3],
                  display: 'flex', flexDirection: 'column', gap: t.s[2], background: t.c.surface,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>Range {idx + 1}</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      minWidth: 24, height: 24, padding: '0 7px', borderRadius: 99,
                      background: t.c.primarySoft, color: t.c.primary,
                      fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{range.score}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Input
                      type="number" value={range.min} min={0} max={range.max}
                      onChange={(e) => updateScoreRange(idx, { min: Number(e.target.value) || 0 })}
                      style={{ minHeight: 32, padding: '5px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                    />
                    <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>–</span>
                    <Input
                      type="number" value={range.max} min={range.min} max={100}
                      onChange={(e) => updateScoreRange(idx, { max: Number(e.target.value) || 100 })}
                      style={{ minHeight: 32, padding: '5px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                    />
                  </div>
                  <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, textAlign: 'center' }}>
                    % achieved → score {range.score}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── Schedule — cycle-based modes only ── */}
        {isCycleMode && (
          <SectionCard
            icon={<CalendarDays size={14} />} accent="yellow"
            title="Monthly schedule"
            subtitle="When each phase of the cycle opens and how long it stays open. Set once — it repeats every month."
          >
            <Row title="Allow overlapping windows" desc="Let Submission, Review and Evaluation run at the same time instead of strictly one after another.">
              <Toggle
                on={goalsSettings.allowWindowOverlap === true}
                onClick={() => updateSettings({ allowWindowOverlap: !goalsSettings.allowWindowOverlap })}
                label="Allow overlapping windows"
              />
            </Row>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: t.s[3], marginTop: t.s[4] }}>
              <WindowCard
                icon={<UserCheck size={14} />} accent="indigo"
                title="Submission window"
                desc="Employees add and submit their goals for the month."
                value={goalsSettings.submissionWindow}
                onChange={(w) => updateSettings({ submissionWindow: w })}
                startDefault={1} durationDefault={15}
              />
              <WindowCard
                icon={<ShieldCheck size={14} />} accent="orange"
                title="Review window"
                desc="Managers approve, reject and (in star mode) assign points."
                value={goalsSettings.reviewWindow}
                onChange={(w) => updateSettings({ reviewWindow: w })}
                startDefault={16} durationDefault={14}
              />
              <WindowCard
                icon={<Trophy size={14} />} accent="green"
                title="Evaluation period"
                desc="Scores are entered and finalised for the month."
                value={goalsSettings.evaluationWindow}
                onChange={(w) => updateSettings({ evaluationWindow: w })}
                startDefault={1} durationDefault={30} maxDuration={45}
                hint="Duration can extend into the next month — e.g. day 30 + 5 days runs to the 4th."
              />
              <WindowCard
                icon={<Clock size={14} />} accent="magenta"
                title="Early submission — next cycle"
                desc="Optionally let people submit next month's goals ahead of time."
                value={goalsSettings.nextCycleEarlySubmission}
                onChange={(w) => updateSettings({ nextCycleEarlySubmission: w })}
                startDefault={null} durationDefault={null} maxDuration={45} clearable
                hint="Example: day 24 for 10 days → opens on the 24th, closes on the 4th of next month. Leave blank to disable."
              />
            </div>
          </SectionCard>
        )}

        {/* ── Workflow — cycle-based modes only ── */}
        {isCycleMode && (
          <SectionCard
            icon={<BrainCircuit size={14} />} accent="indigo"
            title="Performance workflow"
            subtitle="Extra evaluation inputs that feed into the monthly score."
          >
            <Row title="AI goal" desc="Every employee must mark exactly one goal as their AI goal for the month.">
              <Toggle
                on={goalsSettings.aiGoalEnabled === true}
                onClick={() => updateSettings({ aiGoalEnabled: !goalsSettings.aiGoalEnabled })}
                label="AI goal"
              />
            </Row>
            <Row
              title="Soft skills evaluation"
              desc="Managers also rate behavioural competencies each cycle alongside goals."
              last={!(goalsSettings.softSkillsEnabled)}
            >
              <Toggle
                on={goalsSettings.softSkillsEnabled}
                onClick={() => updateSettings({ softSkillsEnabled: !goalsSettings.softSkillsEnabled })}
                label="Soft skills evaluation"
              />
            </Row>

            {mode === 'star' && goalsSettings.softSkillsEnabled && (
              <Row title="Soft-skill star threshold" desc="Every skill must be rated strictly above this (out of 10) to earn the soft-skill ★." >
                <Input
                  type="number" min={0} max={10}
                  value={goalsSettings.softSkillThreshold ?? 5}
                  onChange={(e) => updateSettings({ softSkillThreshold: Math.max(0, Math.min(10, Number(e.target.value) || 0)) })}
                  style={{ width: 72, minHeight: 32, padding: '5px 8px', textAlign: 'center' }}
                />
              </Row>
            )}

            {goalsSettings.softSkillsEnabled && (
              <div style={{ marginTop: t.s[4], paddingTop: t.s[4], borderTop: `1px solid ${t.c.border}` }}>
                <div style={{ fontSize: t.font.size.sm, fontWeight: 500, color: t.c.textSecondary, marginBottom: t.s[3] }}>
                  Competencies rated each cycle
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: t.s[3] }}>
                  {(goalsSettings.softSkills || []).map((skill) => (
                    <div key={skill.id} style={{
                      position: 'relative', border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
                      padding: t.s[3], display: 'flex', flexDirection: 'column', gap: t.s[2], background: t.c.surface,
                    }}>
                      <button
                        onClick={() => removeSoftSkill(skill.id)}
                        title="Remove competency"
                        style={{
                          position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: 99,
                          border: 'none', background: t.c.surfaceMuted, color: t.c.textTertiary,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = t.status.negative; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = t.c.textTertiary; }}
                      >
                        <X size={11} strokeWidth={3} />
                      </button>
                      <Input
                        value={skill.label}
                        placeholder="Competency name"
                        onChange={(e) => updateSoftSkill(skill.id, { label: e.target.value })}
                        style={{ minHeight: 34, padding: '6px 10px', paddingRight: 30, fontWeight: 500 }}
                      />
                      <Textarea
                        value={skill.desc}
                        rows={2}
                        placeholder="What does a high rating look like?"
                        onChange={(e) => updateSoftSkill(skill.id, { desc: e.target.value })}
                        style={{ fontSize: t.font.size.sm, minHeight: 0, padding: '6px 10px' }}
                      />
                    </div>
                  ))}
                  <button
                    onClick={addSoftSkill}
                    style={{
                      minHeight: 108, borderRadius: t.r.md, cursor: 'pointer',
                      border: `1px dashed ${t.c.borderStrong}`, background: t.c.surfaceMuted,
                      color: t.c.textSecondary, fontFamily: t.font.family, fontSize: t.font.size.sm, fontWeight: 500,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                      transition: 'color 140ms, border-color 140ms',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = t.c.primary; e.currentTarget.style.borderColor = t.c.primary; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = t.c.textSecondary; e.currentTarget.style.borderColor = t.c.borderStrong; }}
                  >
                    <Plus size={18} />
                    Add competency
                  </button>
                </div>
              </div>
            )}
          </SectionCard>
        )}

        {/* ── Recognition + notifications — hidden in checklist mode (no scoring) ── */}
        {mode !== 'checklist' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: t.s[5], alignItems: 'start' }}>
            <SectionCard
              icon={<Trophy size={14} />} accent="yellowGreen"
              title="Recognition"
              subtitle="Badges and streaks earned from monthly scores."
            >
              <div style={{ fontSize: t.font.size.sm, fontWeight: 500, color: t.c.textSecondary, marginBottom: t.s[2], display: 'flex', alignItems: 'center', gap: 6 }}>
                <Medal size={13} /> Badge ranges
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[2] }}>
                {(goalsSettings.badgeRanges || []).map((range, index) => (
                  <div key={index} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: t.s[3],
                    border: `1px solid ${t.c.border}`, borderRadius: t.r.md, padding: `${t.s[2]}px ${t.s[3]}px`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2], minWidth: 0 }}>
                      <IconTile
                        url={range.icon?.url} alt={range.label} size={26}
                        onUpload={(file) => uploadBadgeIcon(index, file)}
                        onRemove={() => removeBadgeIcon(index)}
                      />
                      <span style={{ fontSize: t.font.size.base, fontWeight: 500, color: t.c.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {range.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {range.lessThan ? (
                        <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>below</span>
                      ) : (
                        <>
                          <Input
                            type="number" step={0.1} min={0} max={10} value={range.min}
                            onChange={(e) => updateBadgeRange(index, { min: Number(e.target.value) || 0 })}
                            style={{ width: 56, minHeight: 30, padding: '4px 6px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                          />
                          <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>to</span>
                        </>
                      )}
                      <Input
                        type="number" step={0.1} min={0} max={10} value={range.max}
                        onChange={(e) => updateBadgeRange(index, { max: Number(e.target.value) || 10 })}
                        style={{ width: 56, minHeight: 30, padding: '4px 6px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: t.s[4], paddingTop: t.s[4], borderTop: `1px solid ${t.c.border}` }}>
                <div style={{ fontSize: t.font.size.sm, fontWeight: 500, color: t.c.textSecondary, marginBottom: t.s[2], display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Flame size={13} /> Streak points
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: t.s[3] }}>
                  <IconTile
                    url={goalsSettings.streakPoints?.icon?.url} alt="Streak icon" size={32}
                    onUpload={(file) => uploadStreakIcon(file)}
                    onRemove={() => removeStreakIcon()}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2], flex: 1 }}>
                    <Input
                      type="number" min={1} value={goalsSettings.streakPoints?.months}
                      onChange={(e) => updateSettings({ streakPoints: { ...goalsSettings.streakPoints, months: Number(e.target.value) || 1 } })}
                      style={{ width: 64, minHeight: 34, padding: '5px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                    />
                    <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: 'nowrap' }}>consecutive months =</span>
                    <Input
                      type="number" min={1} value={goalsSettings.streakPoints?.points}
                      onChange={(e) => updateSettings({ streakPoints: { ...goalsSettings.streakPoints, points: Number(e.target.value) || 1 } })}
                      style={{ width: 64, minHeight: 34, padding: '5px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                    />
                    <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>points</span>
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              icon={<Bell size={14} />} accent="hotPink"
              title="Notifications"
              subtitle="Automatic nudges around deadlines and achievements."
            >
              <Row title="Goal deadlines" desc="Remind employees before the submission window closes.">
                <Toggle on={goalsSettings.goalDeadlinesAlert} onClick={() => updateSettings({ goalDeadlinesAlert: !goalsSettings.goalDeadlinesAlert })} label="Goal deadlines" />
              </Row>
              <Row title="Review reminders" desc="Nudge managers who still have pending reviews.">
                <Toggle on={goalsSettings.reviewRemindersAlert} onClick={() => updateSettings({ reviewRemindersAlert: !goalsSettings.reviewRemindersAlert })} label="Review reminders" />
              </Row>
              <Row title="Badge milestones" desc="Congratulate people when they reach a new badge.">
                <Toggle on={goalsSettings.badgeMilestonesAlert} onClick={() => updateSettings({ badgeMilestonesAlert: !goalsSettings.badgeMilestonesAlert })} label="Badge milestones" />
              </Row>
              <Row title="Reminder lead time" desc="How many days before a deadline the reminders go out." last>
                <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2] }}>
                  <Input
                    type="number" min={1} max={30} value={goalsSettings.reminderLeadTime}
                    onChange={(e) => updateSettings({ reminderLeadTime: Number(e.target.value) || 3 })}
                    style={{ width: 64, minHeight: 32, padding: '5px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                  />
                  <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>days</span>
                </div>
              </Row>
            </SectionCard>
          </div>
        )}

        {/* ── Footer note ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: t.s[4],
          border: `1px solid ${t.c.border}`, borderRadius: t.r.lg, padding: t.s[4],
          background: t.c.primarySoft,
        }}>
          <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.5 }}>
            <span style={{ fontWeight: t.font.weight.semibold, color: t.c.text }}>Changes apply organisation-wide.</span>{' '}
            Saving updates cycle schedules, scoring and module access for every user immediately.
          </div>
          <Button
            variant="primary"
            leftIcon={<Save size={14} />}
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            style={{ flexShrink: 0 }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </PageShell>
  );
};

export default GoalsSettingsV2;
